import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

/**
 * Submit dropdown — the brutalist `[+] Submit a Tool ▾` control in the
 * header. Mirrors the user-centric delivery taxonomy used on the Submit
 * page (Web / Installable / Agents & AI / Programmatic / Assets).
 *
 * MULTI-SELECT: most real products ship across several surfaces at once
 * (web + api + mcp, say), so you tick every surface that applies and hit
 * "start" — we hand off to /submit?delivery=a,b,c and Submit.jsx pre-checks
 * all of them. (The full form is multi-select too.)
 */
const SUBMIT_GROUPS = [
  {
    key: "web",
    label: "web",
    options: [{ label: "web app", delivery: "web" }],
  },
  {
    key: "installable",
    label: "installable",
    options: [
      { label: "desktop app", delivery: "desktop" },
      { label: "mobile app", delivery: "mobile" },
      { label: "browser extension", delivery: "extension" },
      { label: "cli", delivery: "cli" },
    ],
  },
  {
    key: "agents-ai",
    label: "agents & ai",
    options: [
      { label: "mcp server", delivery: "mcp" },
      { label: "claude skill", delivery: "claude_skill" },
      { label: "gpt", delivery: "chatgpt_gpt" },
    ],
  },
  {
    key: "programmatic",
    label: "programmatic",
    options: [
      { label: "api", delivery: "api" },
      { label: "sdk / library", delivery: "sdk" },
    ],
  },
  {
    key: "assets",
    label: "assets",
    options: [
      { label: "prompt", delivery: "prompt" },
      { label: "workflow", delivery: "workflow" },
      { label: "dataset", delivery: "dataset" },
    ],
  },
];

export default function SubmitDropdown({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const rootRef = useRef(null);
  const nav = useNavigate();

  // Close on outside click + on Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (delivery) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(delivery)) next.delete(delivery);
      else next.add(delivery);
      return next;
    });

  // Hand off to /submit with every ticked surface pre-selected. Submit.jsx
  // reads the comma-separated param on mount and checks each matching chip.
  const start = () => {
    setOpen(false);
    const list = Array.from(selected);
    setSelected(new Set());
    nav(list.length ? `/submit?delivery=${list.join(",")}` : "/submit");
  };

  return (
    <span className={`relative inline-block ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="tv-submit-trigger whitespace-nowrap"
        data-testid="nav-submit"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        [+] Submit<span className="hidden sm:inline"> a Tool</span> <span className="ml-1 text-[#888]">▾</span>
      </button>
      {open && (
        <>
          {/* Mobile-only backdrop — tap anywhere outside the sheet to close. */}
          <div
            className="fixed inset-0 bg-black/30 z-40 sm:hidden"
            onClick={() => setOpen(false)}
            data-testid="submit-dropdown-backdrop"
            aria-hidden="true"
          />
          {/* Mobile: full-width bottom sheet (no corner clipping, action
              button always pinned visible). Desktop (sm+): the original
              right-anchored corner dropdown, unchanged. */}
          <div
            role="menu"
            className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-auto sm:absolute sm:right-0 sm:mt-1 sm:min-w-[240px] flex flex-col max-h-[80vh] sm:max-h-none border-t-2 sm:border border-black bg-white z-50 font-mono text-[12px]"
            data-testid="submit-dropdown-panel"
          >
          <div className="px-3 py-2 sm:py-1 border-b border-black bg-[#fafafa] text-[10px] text-[#888] uppercase tracking-wide flex items-center justify-between shrink-0">
            <span>how do people access it? (pick all that apply)</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="sm:hidden text-black font-bold text-base leading-none px-1 -my-1"
              data-testid="submit-dropdown-close"
              aria-label="close"
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-y-auto sm:flex-none sm:max-h-[320px]">
            {SUBMIT_GROUPS.map((grp, gi) => (
              <div key={grp.key} className={gi > 0 ? "border-t border-[#eee]" : ""}>
                <div
                  className="px-3 pt-1.5 pb-0.5 text-[10px] text-[#888] uppercase tracking-wider"
                  data-testid={`submit-group-${grp.key}`}
                >
                  {grp.label}
                </div>
                {grp.options.map((opt) => {
                  const on = selected.has(opt.delivery);
                  return (
                    <button
                      key={opt.delivery}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={on}
                      onClick={() => toggle(opt.delivery)}
                      className={`block w-full text-left px-3 py-1 whitespace-nowrap ${on ? "bg-black text-white" : "hover:bg-[#f3f3f3]"}`}
                      data-testid={`submit-option-${opt.delivery}`}
                    >
                      {on ? "[x] " : "[ ] "}{opt.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={start}
            className="block w-full text-left px-3 py-2 sm:py-1.5 border-t border-black bg-[#fafafa] hover:bg-black hover:text-white font-bold shrink-0"
            data-testid="submit-start-btn"
          >
            {selected.size > 0
              ? `start with ${selected.size} selected →`
              : "open the form →"}
          </button>
          <Link
            to="/submit"
            onClick={() => { setSelected(new Set()); setOpen(false); }}
            className="block px-3 py-2 sm:py-1 border-t border-[#ddd] text-[11px] text-[#777] hover:bg-black hover:text-white shrink-0"
            data-testid="submit-option-other"
          >
            not sure / something else →
          </Link>
        </div>
        </>
      )}
    </span>
  );
}
